import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./SavedJob.css";
import { resolveLogoSrc } from "../../../../../utils/logoUtils";

/* ─────────────────────────────────────────
   SHARED CONFIG (mirrors JobsPage exactly)
───────────────────────────────────────── */
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

const LOGO_COLORS = [
  "#263012","#263012","#111111","#1d240e","#4a5e20",
  "#263012","#2a2a2a","#5c774a","#263012","#6b7280",
];

/* ─────────────────────────────────────────
   AUTH / STORAGE  (mirrors JobsPage)
───────────────────────────────────────── */
function readStoredToken() {
  return (
    localStorage.getItem("th_employer_token") ||
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") || ""
  );
}
function readStoredUser() {
  try {
    const r =
      localStorage.getItem("th_employer") ||
      localStorage.getItem("th_user") ||
      localStorage.getItem("user") ||
      localStorage.getItem("authUser");
    return r ? JSON.parse(r) : null;
  } catch { return null; }
}
function saveUser(user, token) {
  try {
    const u = JSON.stringify(user);
    localStorage.setItem("th_user", u);
    localStorage.setItem("user", u);
    localStorage.setItem("th_token", token);
    localStorage.setItem("token", token);
  } catch {}
}
function loadPersistedIds(key) {
  try { const r = localStorage.getItem(key); return r ? new Set(JSON.parse(r)) : new Set(); }
  catch { return new Set(); }
}
function persistIds(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

/* ─────────────────────────────────────────
   API HELPERS
───────────────────────────────────────── */
function authFetch(path, opts = {}) {
  const token = readStoredToken();
  return fetch(`${API_BASE}/api${path}`, {
    credentials: "include", ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

async function fetchFullProfile() {
  const token = readStoredToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/candidate/profile`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    ["skills","locations","education","courses"].forEach(k => {
      if (typeof data[k] === "string") { try { data[k] = JSON.parse(data[k]); } catch { data[k] = []; } }
    });
    if (data.first_name && !data.firstName)   data.firstName  = data.first_name;
    if (data.last_name  && !data.lastName)    data.lastName   = data.last_name;
    if (data.headline   && !data.bio)         data.bio        = data.headline;
    if (data.mobile     && !data.phone)       data.phone      = data.mobile;
    if (data.work_status && !data.experience) data.experience = data.work_status;
    if (data.country_iso && !data.country)    data.country    = data.country_iso;
    return data;
  } catch { return null; }
}

/* ─────────────────────────────────────────
   PROFILE HELPERS  (mirrors JobsPage)
───────────────────────────────────────── */
function getMissingFields(user) {
  if (!user) return [];
  const m = [];
  if (!(user.name || user.firstName || user.first_name)) m.push({ key:"name",      label:"Full Name",           icon:"👤" });
  if (!user.email)                                        m.push({ key:"email",     label:"Email Address",       icon:"✉️" });
  if (!(user.phone || user.mobile))                       m.push({ key:"phone",     label:"Phone Number",        icon:"📱" });
  const s = user.skills;
  const hasSkills = Array.isArray(s) ? s.length > 0 : (typeof s==="string" ? s.trim()!==""&&s!=="[]" : !!s);
  if (!hasSkills)                                         m.push({ key:"skills",    label:"Skills",              icon:"🛠️" });
  if (!(user.resume_url || user.resumeUrl))               m.push({ key:"resume",    label:"Resume / CV",         icon:"📄" });
  if (!(user.bio||user.headline||user.description||user.about)) m.push({ key:"bio", label:"Short Description",  icon:"📝" });
  if (!(user.aadhar||user.aadhaar||user.aadhar_number))   m.push({ key:"aadhar",   label:"Aadhaar Card Number", icon:"🪪" });
  if (!(user.pan||user.pan_card||user.pan_number))         m.push({ key:"pan",      label:"PAN Card Number",     icon:"💳" });
  const edu = user.education || user.educations || [];
  const hasEdu = Array.isArray(edu) ? edu.length>0&&edu.some(e=>e?.degree||e?.course) : !!user.degree;
  if (!hasEdu)                                            m.push({ key:"education", label:"Education Details",   icon:"🎓" });
  if (!(user.country||user.country_iso||user.countryIso)) m.push({ key:"country",  label:"Country",             icon:"🌍" });
  if (!user.gender)                                       m.push({ key:"gender",    label:"Gender",              icon:"⚧️" });
  return m;
}
function profilePercent(user) {
  if (!user) return 0;
  const sv=(()=>{ const s=user.skills; if(Array.isArray(s)) return s.length>0?"y":null; if(typeof s==="string") return s.trim()&&s!=="[]"?"y":null; return s||null; })();
  const ev=(()=>{ const e=user.education||user.educations||[]; if(Array.isArray(e)) return e.length>0&&e.some(x=>x?.degree||x?.course)?"y":null; return user.degree||null; })();
  const fields=[
    user.name||user.firstName||user.first_name, user.email, user.phone||user.mobile,
    sv, user.resume_url||user.resumeUrl, user.bio||user.headline||user.description||user.about,
    user.aadhar||user.aadhaar||user.aadhar_number, user.pan||user.pan_card||user.pan_number,
    ev, user.country||user.country_iso||user.countryIso, user.gender,
    user.location||user.city, user.experience||user.work_status,
  ];
  return Math.round(fields.filter(v=>v&&String(v).trim()!=="").length/fields.length*100);
}
function displayName(user) {
  if (!user) return "";
  return user.name||`${user.firstName||user.first_name||""} ${user.lastName||user.last_name||""}`.trim()||user.username||user.email||"";
}

/* ─────────────────────────────────────────
   JOB NORMALISER  (mirrors JobsPage)
───────────────────────────────────────── */
function formatPostedDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;

  return dt.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
function isNew(d) { return !!d&&Math.floor((Date.now()-new Date(d))/86400000)<=3; }
function companyColor(n) { return LOGO_COLORS[(n||"?").charCodeAt(0)%LOGO_COLORS.length]; }

function normaliseJob(raw) {
  let tags=[];
  try {
    const rt=raw.skills||raw.tags||"";
    tags=typeof rt==="string"
      ? rt.startsWith("[") ? JSON.parse(rt) : rt.split(",").map(t=>t.trim()).filter(Boolean)
      : Array.isArray(rt) ? rt : [];
  } catch { tags=[]; }
  const locLower=(raw.location||"").toLowerCase();
  const locationType=locLower.includes("hybrid")?"Hybrid":locLower.includes("remote")?"Remote":"On-site";
  const salaryMin=raw.min_salary||0; const salaryMax=raw.max_salary||0;
  const sMin=Math.round(salaryMin/100000); const sMax=Math.round(salaryMax/100000);
  const salaryAvg=sMin&&sMax?Math.round((sMin+sMax)/2):sMin||sMax;
  const salaryDisplay=(()=>{
    const r=(raw.salary||"").replace(/^[$?₹\u20B9\s]+/,"").trim();
    if(r) return `₹${r}`; if(sMin&&sMax) return `₹${sMin}–${sMax} LPA`;
    if(salaryAvg) return `₹${salaryAvg} LPA`; return "Not disclosed";
  })();
  const postedAt =
  raw.posted_date ||
  raw.created_at ||
  raw.posted_at ||
  raw.updated_at ||
  // raw.saved_at ||
  raw.job?.posted_date ||
  raw.job?.created_at ||
  null;
  return {
    id:raw.id||raw._id, title:raw.title||"Untitled",
    company:raw.company_name||raw.company||"—",
    dept:raw.category||raw.department||"General",
    type:raw.type||raw.job_type||"Full-time",
    location:raw.location||"", locationType,
    salaryMin,salaryMax,salaryAvg,salary:salaryDisplay,tags,
    status:raw.status||"active",postedAt,
    postedLabel:formatPostedDate(postedAt),isNew:isNew(postedAt),
    description:raw.description||"",experience:raw.experience||"",
    openings:Number(raw.openings||raw.vacancies||1),
    applicantCount:Number(raw.applicant_count||0),
    recruiter_id:raw.recruiter_id||raw.employer_id||null,
    recruiter_email:raw.recruiter_email||raw.employer_email||null,
    logo:resolveLogoSrc(raw.company_logo||raw.ep_logo||raw.logo||""),
  };
}

/* ─────────────────────────────────────────
   APPLY API  (mirrors JobsPage exactly)
───────────────────────────────────────── */
async function submitApplicationAPI({ job }) {
  const res=await authFetch("/applications",{
    method:"POST",
    body:JSON.stringify({job_id:job.id,recruiter_id:job.recruiter_id,applied_at:new Date().toISOString()}),
  });
  let data={}; try{data=await res.json();}catch{}
  if(res.status===409) return{success:true,alreadyApplied:true,application:{job_title:job.title,company:job.company}};
  if(res.status===422&&data.profileIncomplete) return{success:false,profileIncomplete:true,missing:data.missing||[],error:data.error};
  if(!res.ok){const msg=data.detail||data.error||data.message||`Server error ${res.status}`;throw new Error(msg);}
  return{success:true,alreadyApplied:false,application:{job_title:data.application?.job_title||data.job_title||job.title,company:data.application?.company||data.company||job.company,id:data.application?.id||data.id||null}};
}

async function sendEmailsAPI({user,job}){
  try{
    await authFetch("/email/job-application",{
      method:"POST",
      body:JSON.stringify({
        admin_email:"ihn.socialmediaa@gmail.com",user_email:user.email,
        user_name:displayName(user),user_phone:user.phone||user.mobile||"N/A",
        user_skills:Array.isArray(user.skills)?user.skills.join(", "):(user.skills||"N/A"),
        user_experience:user.experience||user.work_status||"N/A",
        user_resume:user.resume_url||"N/A",
        job_title:job.title,job_company:job.company,job_id:job.id,
        recruiter_id:job.recruiter_id,recruiter_email:job.recruiter_email,
        applied_at:new Date().toISOString(),
      }),
    });
  }catch(e){console.warn("Email notification failed (non-fatal):",e.message);}
}

/* ─────────────────────────────────────────
   PROFILE-INCOMPLETE POPUP  (JobsPage identical)
───────────────────────────────────────── */
function ProfileIncompletePopup({open,onClose,user,targetJob,serverMissing}){
  useEffect(()=>{document.body.style.overflow=open?"hidden":"";return()=>{document.body.style.overflow="";};},[open]);
  useEffect(()=>{const fn=e=>{if(e.key==="Escape")onClose();};window.addEventListener("keydown",fn);return()=>window.removeEventListener("keydown",fn);},[onClose]);
  if(!open) return null;
  const LABEL_MAP={"Full Name":{key:"name",icon:"👤"},"Email":{key:"email",icon:"✉️"},"Phone Number":{key:"phone",icon:"📱"},"Skills":{key:"skills",icon:"🛠️"},"Resume":{key:"resume",icon:"📄"},"Short Description":{key:"bio",icon:"📝"},"Aadhaar Number":{key:"aadhar",icon:"🪪"},"PAN Number":{key:"pan",icon:"💳"},"Education Details":{key:"education",icon:"🎓"},"Country":{key:"country",icon:"🌍"},"Gender":{key:"gender",icon:"⚧️"}};
  const missing=serverMissing&&serverMissing.length>0?serverMissing.map(l=>({...(LABEL_MAP[l]||{key:l,icon:"❗"}),label:l})):getMissingFields(user);
  const pct=profilePercent(user);
  return(
    <div className="pip-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="pip-modal" role="dialog" aria-modal="true">
        <div className="pip-accent-bar"/>
        <button className="pip-close" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        <div className="pip-header">
          <div className="pip-header__icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg></div>
          <div className="pip-header__text">
            <h2 className="pip-header__title">Profile Incomplete</h2>
            <p className="pip-header__sub">{targetJob?<>To apply for <strong>{targetJob.title}</strong> at <strong>{targetJob.company}</strong>, complete missing details first.</>:"Fill in required details to start applying."}</p>
          </div>
        </div>
        <div className="pip-progress">
          <div className="pip-progress__row"><span className="pip-progress__label">Profile Strength</span><span className="pip-progress__pct" style={{color:pct<50?"#263012":pct<80?"#263012":"#111111"}}>{pct}%</span></div>
          <div className="pip-progress__track"><div className="pip-progress__fill" style={{width:`${pct}%`,background:pct<50?"#263012":pct<80?"#263012":"#111111"}}/></div>
          <p className="pip-progress__note">{11-missing.length} of 11 required fields filled</p>
        </div>
        <div className="pip-body">
          <p className="pip-body__heading"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>These fields are missing</p>
          <div className="pip-fields-list">
            {missing.map((f,i)=>(
              <div key={i} className="pip-field-row">
                <div className="pip-field-row__left"><span className="pip-field-row__icon">{f.icon}</span><span className="pip-field-row__label">{f.label}</span></div>
                <span className="pip-field-row__tag">Required</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pip-footer">
          <a href="/employee/profile" className="pip-btn pip-btn--primary"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>Complete My Profile</a>
          <button className="pip-btn pip-btn--ghost" onClick={onClose}>Maybe Later</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   SUCCESS POPUP  (JobsPage identical)
───────────────────────────────────────── */
function SuccessPopup({open,job,onClose}){
  useEffect(()=>{if(!open) return;document.body.style.overflow="hidden";const t=setTimeout(onClose,10000);return()=>{clearTimeout(t);document.body.style.overflow="";};},[open,onClose]);
  if(!open||!job) return null;
  return(
    <div className="jp-success-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="jp-success-modal">
        <div className="jp-success__confetti" aria-hidden="true">
          {Array.from({length:18},(_,i)=>(
            <div key={i} className="jp-success__piece" style={{"--i":i,"--c":["#263012","#263012","#111111","#1d240e","#263012","#263012","#2a2a2a","#5c774a","#263012"][i%9]}}/>
          ))}
        </div>
        <button className="jp-success__close" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        <div className="jp-success__top">
          <div className="jp-success__emoji">🎉</div>
          <div className="jp-success__check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="22" height="22"><polyline points="20 6 9 17 4 12"/></svg></div>
        </div>
        <div className="jp-success__content">
          <h3 className="jp-success__title">Application Submitted!</h3>
          <p className="jp-success__desc">You've successfully applied for <strong>{job.title||job.job_title}</strong> at <strong>{job.company}</strong>.</p>
          <p className="jp-success__note">The recruiter will review your profile and reach out if you're shortlisted.</p>
        </div>
        <div className="jp-success__actions">
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--primary"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>View My Applications</a>
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--outline">Complete Profile</a>
        </div>
        <div className="jp-success__bar"/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   JOB DETAIL MODAL  (JobsPage identical)
───────────────────────────────────────── */
function JobModal({job,onClose,savedJobs,appliedJobs,applyingJobId,onToggleSave,onApplyClick}){
  const isSaved=savedJobs.has(job.id);
  const isApplied=appliedJobs.has(job.id);
  const isApplying=applyingJobId===job.id;
  const color=companyColor(job.company);
  useEffect(()=>{
    document.body.style.overflow="hidden";
    const fn=e=>{if(e.key==="Escape")onClose();};
    window.addEventListener("keydown",fn);
    return()=>{window.removeEventListener("keydown",fn);document.body.style.overflow="";};
  },[onClose]);
  return(
    <div className="jp-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="jp-modal" role="dialog" aria-modal="true">
        <div className="jp-modal__head" style={{"--acc":color}}>
          <div className="jp-modal__logo" style={{background:`${color}15`,borderColor:`${color}30`}}>
            {job.logo?<img src={job.logo} alt={job.company} style={{width:"100%",height:"100%",objectFit:"contain",borderRadius:"inherit"}}/>:<span style={{color,fontWeight:900,fontSize:"1.3rem"}}>{(job.company||"?")[0]}</span>}
          </div>
          <div className="jp-modal__head-text">
            <div className="jp-modal__title">{job.title}</div>
            <div className="jp-modal__company">{job.company}</div>
          </div>
          <button className="jp-modal__close" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>
        <div className="jp-modal__body">
          <div className="jp-modal__chips">
            <span className="jp-meta-chip jp-meta-chip--dept" style={{color,background:`${color}12`}}>{job.dept}</span>
            <span className="jp-meta-chip jp-meta-chip--type">{job.type}</span>
            {job.location&&<span className="jp-meta-chip jp-meta-chip--loc">📍 {job.location}</span>}
            {job.experience&&<span className="jp-meta-chip jp-meta-chip--exp">{job.experience}</span>}
            {job.locationType!=="On-site"&&<span className="jp-meta-chip">{job.locationType}</span>}
          </div>
          <div className="jp-modal__meta-row">
            <div className="jp-modal__meta-item">
              <div className="jp-modal__meta-icon" style={{background:color+"18",color}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div><div className="jp-modal__meta-label">Vacancies</div><div className="jp-modal__meta-val" style={{color}}>{job.openings===1?"1 opening":`${job.openings} openings`}</div></div>
            </div>
            {job.salary!=="Not disclosed"&&(
              <div className="jp-modal__meta-item">
                <div className="jp-modal__meta-icon" style={{background:"#f0fdf4",color:"#16a34a"}}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div>
                <div><div className="jp-modal__meta-label">Salary</div><div className="jp-modal__meta-val" style={{color:"#16a34a"}}>{job.salary}</div></div>
              </div>
            )}
               {job.postedLabel&&(
              <div className="jp-modal__meta-item">
                <div className="jp-modal__meta-icon" style={{background:"#f0f0f8",color:"#6b7280"}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div>
                  <div className="jp-modal__meta-label">Posted</div>
                  <div className="jp-modal__meta-val">{job.postedLabel}{job.isNew&&<span className="jp-modal__new-badge">NEW</span>}</div>
                </div>
              </div>
            )}
          </div>
          {job.description&&<div className="jp-modal__section"><div className="jp-modal__section-title">About this role</div><div className="jp-modal__desc">{job.description}</div></div>}
          {job.tags.length>0&&<div className="jp-modal__section"><div className="jp-modal__section-title">Skills &amp; Technologies</div><div className="jp-modal__tags">{job.tags.map((t,i)=><span key={i} className="jp-tag">{t}</span>)}</div></div>}
        </div>
        <div className="jp-modal__footer">
          {isApplied
            ?<button className="jp-btn jp-btn--applied" disabled>✓ Already Applied</button>
            :isApplying
            ?<button className="jp-btn jp-btn--primary" disabled style={{opacity:0.75}}><span className="jpc__spinner" style={{marginRight:6}}/>Applying…</button>
            :<button className="jp-btn jp-btn--primary" onClick={()=>{onClose();onApplyClick(job);}}>Apply Now →</button>}
          <button
            className={`jp-icon-btn${isSaved?" jp-icon-btn--saved":""}`}
            style={isSaved?{color,borderColor:color,background:`${color}12`}:{}}
            onClick={()=>onToggleSave(job)} title={isSaved?"Remove saved":"Save job"}>
            <svg viewBox="0 0 24 24" fill={isSaved?"currentColor":"none"} stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
          </button>
          <button className="jp-btn jp-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   JOB CARD  (pixel-identical to JobsPage jpc)
───────────────────────────────────────── */
function JobCard({job,savedJobs,appliedJobs,applyingJobId,onToggleSave,onApplyClick,onViewDesc}){
  const isSaved=savedJobs.has(job.id);
  const isApplied=appliedJobs.has(job.id);
  const isApplying=applyingJobId===job.id;
  const color=companyColor(job.company);
  const initial=(job.company||"?")[0].toUpperCase();
  return(
    <div className={`jpc${isApplied?" jpc--applied":""}`} style={{"--acc":color}}>
      <div className="jpc__stripe"/>
      <button
        className={`jpc__bmark${isSaved?" jpc__bmark--on":""}`}
        onClick={e=>{e.stopPropagation();onToggleSave(job);}}
        title={isSaved?"Remove saved":"Save job"}>
        <svg viewBox="0 0 24 24" fill={isSaved?"currentColor":"none"} stroke="currentColor" strokeWidth="2.2" width="16" height="16"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
      </button>

      <div className="jpc__header">
        <div className="jpc__logo" style={{background:`${color}14`,borderColor:`${color}30`}}>
          {job.logo
            ?<img src={job.logo} alt={job.company} style={{width:"100%",height:"100%",objectFit:"contain",borderRadius:8}}/>
            :<span style={{color,fontWeight:900,fontSize:"1.1rem"}}>{initial}</span>}
        </div>
        <div className="jpc__header-info">
          <div className="jpc__company">{job.company}</div>
          {/* {job.postedLabel&&(
            <div className={`jpc__posted-date${job.isNew?" jpc__posted-date--new":""}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {job.isNew&&<span className="jpc__new-dot"/>}
              {job.postedLabel}
            </div>
          )} */}
        </div>
        {isApplied
          ?<span className="jpc__status jpc__status--applied">✓ Applied</span>
          :job.status==="active"
          ?<span className="jpc__status jpc__status--active">● Hiring</span>
          :<span className="jpc__status jpc__status--paused">⏸ Paused</span>}
      </div>

      <h3 className="jpc__title">{job.title}</h3>
      <span className="jpc__dept" style={{color,background:`${color}12`,borderColor:`${color}25`}}>{job.dept}</span>

      <div className="jpc__meta">
        {job.location&&<span className="jpc__chip jpc__chip--loc"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{job.location}</span>}
        <span className="jpc__chip jpc__chip--type">{job.type}</span>
        {job.locationType!=="On-site"&&<span className={`jpc__chip${job.locationType==="Remote"?" jpc__chip--remote":" jpc__chip--hybrid"}`}>{job.locationType==="Remote"?"🌐 Remote":"🔄 Hybrid"}</span>}
        {job.experience&&<span className="jpc__chip jpc__chip--exp"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>{job.experience}</span>}
      </div>

      {job.salary!=="Not disclosed"&&<div className="jpc__salary" style={{color}}>{job.salary}</div>}

      {job.tags.length>0&&(
        <div className="jpc__tags">
          {job.tags.slice(0,4).map((t,i)=><span key={i} className="jpc__tag">{t}</span>)}
          {job.tags.length>4&&<span className="jpc__tag jpc__tag--more">+{job.tags.length-4}</span>}
        </div>
      )}

      <div className="jpc__info-bar">
        <span className="jpc__vacancy">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          {job.openings===1?"1 vacancy":`${job.openings} vacancies`}
        </span>
        <span className="jpc__info-sep">·</span>
        {job.postedLabel&&(
          <span className={`jpc__date-label${job.isNew?" jpc__date-label--new":""}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Posted {job.postedLabel}
          </span>
        )}
      </div>

      <div className="jpc__actions">
        <button className="jpc__view" onClick={()=>onViewDesc(job)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          View Details
        </button>
        {isApplied?(
          <button className="jpc__apply jpc__apply--done" disabled>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="20 6 9 17 4 12"/></svg>
            Applied
          </button>
        ):isApplying?(
          <button className="jpc__apply jpc__apply--applying" disabled>
            <span className="jpc__spinner"/>Applying…
          </button>
        ):(
          <button className="jpc__apply" onClick={()=>onApplyClick(job)} disabled={job.status==="paused"}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            Apply Now
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
export default function SavedJobsPage() {
  const navigate = useNavigate();

  const [user, setUser]           = useState(() => readStoredUser());
  const [savedJobs, setSavedJobs] = useState(() => loadPersistedIds("jp_saved"));
  const [appliedJobs, setAppliedJobs] = useState(() => loadPersistedIds("jp_applied"));
  const [applyingJobId, setApplyingJobId] = useState(null);
  const applyingRef = useRef(new Set());

  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [selectedJob, setSelectedJob] = useState(null);
  const [pendingJob, setPendingJob]   = useState(null);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [serverMissing, setServerMissing]       = useState([]);
  const [successData, setSuccessData]           = useState({open:false,job:null});
  const [errorToast, setErrorToast]             = useState({open:false,message:""});

  /* persist */
  useEffect(()=>{persistIds("jp_applied",appliedJobs);},[appliedJobs]);
  useEffect(()=>{persistIds("jp_saved",savedJobs);},[savedJobs]);

  /* auth sync */
  useEffect(()=>{
    const stored = readStoredUser();
    if (stored) {
      setUser(stored);
      fetchFullProfile().then(f=>{if(!f) return;const m={...stored,...f};saveUser(m,readStoredToken());setUser(m);});
    }
    const onAuth = () => { const f=readStoredUser(); if(f) setUser(f); };
    window.addEventListener("auth:update", onAuth);
    return () => window.removeEventListener("auth:update", onAuth);
  },[]);

  /* fetch saved jobs */
useEffect(() => {
  if (!readStoredToken()) { 
    setLoading(false); 
    return; 
  }

  setLoading(true);
  setError("");

  authFetch("/saved-jobs")
    .then(r => r.ok ? r.json() : Promise.reject(new Error(`Server error ${r.status}`)))
    .then(data => {
      const raw = data.data || data.jobs || data || [];

      const normalised = raw.map(row => {
        const jobData = {
          ...(row.job || {}),
          ...row
        };

        return normaliseJob({
          ...jobData,
          id: jobData.id || row.job_id
        });
      });

      setJobs(normalised); // ✅ VERY IMPORTANT
    })
    .catch(e => setError(e.message || "Failed to load saved jobs."))
    .finally(() => setLoading(false));

}, []);

  /* sync applied from server */
  useEffect(()=>{
    if (!user) return;
    authFetch("/applications/mine").then(r=>r.ok?r.json():null).then(d=>{
      if(d?.success){const ids=new Set((d.data||[]).map(j=>j.job_id||j.id));setAppliedJobs(prev=>{const m=new Set([...prev,...ids]);persistIds("jp_applied",m);return m;});}
    }).catch(()=>{});
  },[user]);

  /* ── APPLY FLOW (mirrors JobsPage exactly) ── */
  const executeApply = useCallback(async (job, currentUserRef) => {
    if (!job || !currentUserRef) return;
    if (applyingRef.current.has(job.id)) return;
    let alreadyApplied = false;
    setAppliedJobs(cur => { alreadyApplied = cur.has(job.id); return cur; });
    if (alreadyApplied) { setSuccessData({open:true,job:{title:job.title,company:job.company,id:job.id}}); return; }
    applyingRef.current.add(job.id);
    setApplyingJobId(job.id);
    try {
      const fresh = await fetchFullProfile();
      if (fresh) { const merged={...currentUserRef,...fresh}; saveUser(merged,readStoredToken()); setUser(merged); currentUserRef=merged; }
      const result = await submitApplicationAPI({job});
      if (result.alreadyApplied) { setAppliedJobs(prev=>{const n=new Set([...prev,job.id]);persistIds("jp_applied",n);return n;}); setSuccessData({open:true,job:{title:job.title,company:job.company,id:job.id}}); return; }
      if (result.profileIncomplete) { setPendingJob(job); setServerMissing(result.missing||[]); setProfilePopupOpen(true); return; }
      if (!result.success) { setErrorToast({open:true,message:result.error||"Application failed."}); return; }
      setAppliedJobs(prev=>{const n=new Set([...prev,job.id]);persistIds("jp_applied",n);return n;});
      setSuccessData({open:true,job:{title:result.application?.job_title||job.title,company:result.application?.company||job.company,id:job.id}});
      sendEmailsAPI({user:currentUserRef,job});
    } catch(err) {
      let msg = err.message||"Network error.";
      if (msg.includes("401")||msg.includes("Unauthorized")) msg="Session expired. Please log in again.";
      else if (msg.includes("422")) msg="Profile incomplete. Please fill all required fields.";
      else if (msg.includes("500")) msg="Server error. Please try again in a moment.";
      setErrorToast({open:true,message:msg});
    } finally { applyingRef.current.delete(job.id); setApplyingJobId(null); }
  },[]);

  const handleApplyClick = useCallback(job => {
    if (appliedJobs.has(job.id)) return;
    if (!user) {
      setPendingJob(job);
      window.dispatchEvent(new CustomEvent("open-login-drawer"));
      const onLogin = e => {
        const {token,user:u} = e.detail||{}; if(!u||!token) return;
        saveUser(u,token); setUser(u);
        window.removeEventListener("login:success",onLogin);
        fetchFullProfile().then(f=>{const m=f?{...u,...f}:u;saveUser(m,token);setUser(m);executeApply(job,m);});
      };
      window.addEventListener("login:success",onLogin);
      return;
    }
    executeApply(job,user);
  },[user,appliedJobs,executeApply]);

  const handleToggleSave = useCallback(job => {
    if (!user) { window.dispatchEvent(new CustomEvent("open-login-drawer")); return; }
    const saving = !savedJobs.has(job.id);
    setSavedJobs(prev=>{const n=new Set(prev);saving?n.add(job.id):n.delete(job.id);persistIds("jp_saved",n);return n;});
    /* if unsaving, remove from the visible list */
    if (!saving) setJobs(prev => prev.filter(j => j.id !== job.id));
    authFetch("/saved-jobs",{method:saving?"POST":"DELETE",body:JSON.stringify({job_id:job.id})}).catch(()=>{});
  },[user,savedJobs]);

  /* ── RENDER ── */
  const isEmpty = !loading && !error && jobs.length === 0;
  const isLoggedOut = !readStoredToken();

  return (
    <div className="sjp-root">

      {/* ── TOP BAR ── */}
      <div className="sjp-topbar">
        <div className="sjp-topbar__left">
          <button className="sjp-back" onClick={()=>navigate(-1)} title="Go back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="16" height="16"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div className="sjp-heading">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
            <h1 className="sjp-heading__title">Saved Jobs</h1>
            {!loading && !error && jobs.length > 0 && (
              <span className="sjp-heading__badge">{jobs.length}</span>
            )}
          </div>
        </div>
        <div className="sjp-topbar__right">
          {user && (
            <span className="sjp-user-hint">
              Logged in as <strong>{displayName(user)}</strong>
            </span>
          )}
          <a href="/jobs" className="sjp-browse-btn">
            Browse All Jobs
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </a>
        </div>
      </div>

      {/* ── LOADING ── */}
      {loading && (
        <div className="sjp-state">
          <div className="jp-spinner"/>
          <p>Loading your saved jobs…</p>
        </div>
      )}

      {/* ── NOT LOGGED IN ── */}
      {!loading && isLoggedOut && (
        <div className="sjp-state">
          <div className="sjp-state__icon">🔒</div>
          <h2 className="sjp-state__title">Sign in to view saved jobs</h2>
          <p className="sjp-state__sub">Your saved jobs are stored with your account.</p>
          <button className="sjp-cta-btn" onClick={()=>window.dispatchEvent(new CustomEvent("open-login-drawer"))}>
            Log in to continue
          </button>
        </div>
      )}

      {/* ── ERROR ── */}
      {!loading && error && (
        <div className="sjp-state">
          <div className="sjp-state__icon">⚠️</div>
          <h2 className="sjp-state__title">Couldn't load saved jobs</h2>
          <p className="sjp-state__sub">{error}</p>
          <button className="sjp-cta-btn" onClick={()=>window.location.reload()}>Try again</button>
        </div>
      )}

      {/* ── EMPTY ── */}
      {isEmpty && !isLoggedOut && (
        <div className="sjp-state">
          <div className="sjp-state__icon">🔖</div>
          <h2 className="sjp-state__title">No saved jobs yet</h2>
          <p className="sjp-state__sub">Bookmark jobs while browsing and they'll appear here.</p>
          <a href="/jobs" className="sjp-cta-btn">Browse open jobs →</a>
        </div>
      )}

      {/* ── GRID ── */}
      {!loading && !error && jobs.length > 0 && (
        <div className="sjp-grid-wrap">
          <p className="sjp-sub">
            {jobs.filter(j=>appliedJobs.has(j.id)).length > 0 && (
              <span className="sjp-applied-note">
                ✓ {jobs.filter(j=>appliedJobs.has(j.id)).length} applied
              </span>
            )}
          </p>
          <div className="jp-grid">
            {jobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                savedJobs={savedJobs}
                appliedJobs={appliedJobs}
                applyingJobId={applyingJobId}
                onToggleSave={handleToggleSave}
                onApplyClick={handleApplyClick}
                onViewDesc={setSelectedJob}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── JOB DETAIL MODAL ── */}
      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={()=>setSelectedJob(null)}
          savedJobs={savedJobs}
          appliedJobs={appliedJobs}
          applyingJobId={applyingJobId}
          onToggleSave={handleToggleSave}
          onApplyClick={handleApplyClick}
        />
      )}

      {/* ── PROFILE INCOMPLETE ── */}
      <ProfileIncompletePopup
        open={profilePopupOpen}
        onClose={()=>{setProfilePopupOpen(false);setPendingJob(null);setServerMissing([]);}}
        user={user}
        targetJob={pendingJob}
        serverMissing={serverMissing}
      />

      {/* ── SUCCESS ── */}
      <SuccessPopup
        key={`${successData.job?.id}-${successData.open}`}
        open={successData.open}
        job={successData.job}
        onClose={()=>setSuccessData({open:false,job:null})}
      />

      {/* ── ERROR TOAST ── */}
      {errorToast.open && (
        <div className="sjp-error-toast">
          <span>⚠️</span>
          <span>{errorToast.message}</span>
          <button onClick={()=>setErrorToast({open:false,message:""})}>✕</button>
        </div>
      )}
    </div>
  );
}